import { useField } from 'formik';
import {
  castArray,
  filter,
  includes,
  intersection,
  isEmpty,
  map,
} from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikSelect, FormikSelectProps, StatusLabel } from 'components';
import { OrderType } from 'enums';
import { useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { P2PProviderTradeMethods } from 'types';
import { p2pProviderUtils, P2PProviderDirection } from 'utils';

const STATUS_CHIP_SX = { ml: 1, fontSize: '0.65rem', height: 18 };

type Props = {
  name: string;
  label: string;
  assetCurrencyIds?: string[];
  exclude?: string[];
  tradeMethodsIds?: string[];
  tradeMethodsByProviderId?: Record<string, P2PProviderTradeMethods>;
  ignoreTradeMethodsIds?: boolean;
  hideArchived?: boolean;
  // Omit entirely to skip the active/inactive check altogether (e.g. contexts unrelated to
  // this feature) — pass 'payin' / 'payout' to check one direction, or 'any' for either.
  direction?: P2PProviderDirection;
} & Partial<FormikSelectProps>;

export const P2PProviderSelect: React.FC<Props> = ({
  name,
  label,
  assetCurrencyIds,
  exclude = [],
  tradeMethodsIds = [],
  tradeMethodsByProviderId = {},
  ignoreTradeMethodsIds = false,
  hideArchived = false,
  direction,
  ...rest
}) => {
  const { p2pProviders } = useUserContext();
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const [{ value: selectedValue }] = useField(name);

  const availableP2PProviders = useMemo(
    () =>
      filter(p2pProviders, (p2pProvider) => {
        if (!ignoreTradeMethodsIds) {
          const providerTradeMethods = tradeMethodsByProviderId[p2pProvider.id];
          const p2pProviderTradeMethodsId = [
            ...(providerTradeMethods?.payinConfig?.tradeMethodIds || []),
            ...(providerTradeMethods?.payoutConfig?.tradeMethodIds || []),
          ];

          if (
            !intersection(tradeMethodsIds, p2pProviderTradeMethodsId).length
          ) {
            return false;
          }
        }

        if (
          !isEmpty(assetCurrencyIds) &&
          !includes(assetCurrencyIds, p2pProvider.assetCurrencyId)
        ) {
          return false;
        }

        if (exclude?.includes(p2pProvider.id)) {
          return false;
        }

        // Keep the currently selected provider even if archived — otherwise the input renders
        // empty over a non-empty form value and the inactive warning never shows.
        if (
          hideArchived &&
          p2pProvider.archived &&
          !includes(castArray(selectedValue), p2pProvider.id)
        ) {
          return false;
        }

        return true;
      }),
    [
      p2pProviders,
      tradeMethodsByProviderId,
      tradeMethodsIds,
      assetCurrencyIds,
      exclude,
      ignoreTradeMethodsIds,
      hideArchived,
      selectedValue,
    ],
  );

  const providersOptions = useMemo(
    () =>
      map(availableP2PProviders, (p2pProvider) => {
        const option = { label: p2pProvider.name, value: p2pProvider.id };

        // No `direction` at all means this caller doesn't opt into the active/inactive
        // check — leave the option untouched (no disabling, no children).
        if (!direction) {
          return option;
        }

        return {
          ...option,
          disabled: p2pProviderUtils.isInactive(p2pProvider, direction),
          children: [
            direction !== OrderType.Payout && (
              <StatusLabel
                key="payin"
                status={
                  p2pProvider.payinConfig?.enabled ? 'active' : 'inactive'
                }
                label={tCommon('common.payin')}
                size="small"
                sx={STATUS_CHIP_SX}
              />
            ),
            direction !== OrderType.Payin && (
              <StatusLabel
                key="payout"
                status={
                  p2pProvider.payoutConfig?.enabled ? 'active' : 'inactive'
                }
                label={tCommon('common.payout')}
                size="small"
                sx={STATUS_CHIP_SX}
              />
            ),
          ],
        };
      }),
    [availableP2PProviders, direction, tCommon],
  );

  return (
    <FormikSelect
      noneOption
      label={label}
      {...rest}
      name={name}
      options={providersOptions}
      optionDisabledText={tCommon('errors.p2p_provider_inactive_warning')}
    />
  );
};
